<script setup lang="ts">
defineOptions({
	name: 'InformationSection',
})

const { t: $t, locale } = useI18n()
</script>

<template>
	<section class="space-y-7">
		<div class="flex items-center gap-3.5">
			<UIcon
				name="i-carbon:manage-protection"
				class="size-7 text-[#ED8B00]"
			/>
			<h2 class="text-xl sm:text-2xl font-medium text-gray-900 tracking-tight">
				{{ $t('What Information do we Collect') }}
			</h2>
		</div>
		<div class="space-y-5 text-base sm:text-[18px] text-[#364153] tracking-[-0.199px] leading-7">
			<p>
				{{
					$t(
						`Gaveta Decimal only processes Users' Personal Data when duly authorised to do so. For the lawful processing of Personal Data, the GDPR requires an adequate legal basis for each specific processing activity`,
					)
				}}.
			</p>

			<p>
				{{ $t('The processing of Personal Data is lawful when at least one of the following conditions is met')
				}}{{ locale === 'fr' ? ' :' : ':' }}
			</p>

			<ul class="list-disc pl-7 space-y-2.5">
				<li>
					{{
						$t(
							'The Data Subject has given explicit consent to the processing of their Personal Data for one or more specific purposes',
						)
					}}.
				</li>

				<li>
					{{
						$t(
							'The processing is necessary for the performance of a contract to which the Data Subject is a party or to take steps at the request of the Data Subject before entering into a contract',
						)
					}}.
				</li>

				<li>
					{{
						$t('The processing is necessary for compliance with a legal obligation to which the Controller is subject')
					}}.
				</li>

				<li>
					{{
						$t(
							'The processing is necessary to protect the vital interests of the Data Subject or another natural person',
						)
					}}.
				</li>
				<li>
					{{
						$t(
							`The processing is necessary for the purposes of Gaveta Decimal’s legitimate interests or those of Third Parties (except where such interests are overridden by the interests or fundamental rights and freedoms of the Data Subject, which require the protection of Personal Data)`,
						)
					}}.
				</li>
			</ul>

			<p>
				{{ $t('In this context, the following Personal Data is processed on the Platform')
				}}{{ locale === 'fr' ? ' :' : ':' }}
			</p>
			<div class="smm:hidden">
				<p class="font-semibold">{{ $t('Data Category') }}</p>
				<ul class="list-disc pl-7 space-y-2.5">
					<li>{{ $t('Contact Data, such as Name, Email address, and other identifiers of the User') }}</li>
					<li>{{ $t('Navigation/Cookie Data - data collected via cookies or similar technologies') }}</li>
					<li>{{ $t('Payment Data (if applicable)') }}</li>
				</ul>

				<p class="font-semibold">{{ $t('Purpose(s)') }}</p>
				<ul class="list-disc pl-7 space-y-2.5">
					<li>{{ $t('Account management, communications') }}</li>
					<li>{{ $t('Personalization, analytics') }}</li>
					<li>{{ $t('Transaction processing') }}</li>
				</ul>

				<p class="font-semibold">{{ $t('Lawful Basis') }}</p>
				<ul class="list-disc pl-7 space-y-2.5">
					<li>{{ $t('Performance of contract, consent') }}</li>
					<li>{{ $t('Consent, legitimate interest') }}</li>
					<li>{{ $t('Performance of contract') }}</li>
				</ul>
			</div>

			<table class="hidden smm:block border-collapse w-full">
				<thead class="text-left">
					<tr>
						<th class="border border-slate-600 p-2">{{ $t('Data Category') }}</th>
						<th class="border border-slate-600 p-2">{{ $t('Purpose(s)') }}</th>
						<th class="border border-slate-600 p-2">{{ $t('Lawful Basis') }}</th>
					</tr>
				</thead>
				<tbody class="text-base">
					<tr>
						<td class="border border-slate-700 p-2">
							{{ $t('Contact Data, such as Name, Email address, and other identifiers of the User') }}
						</td>
						<td class="border border-slate-700 p-2">{{ $t('Account management, communications') }}</td>
						<td class="border border-slate-700">{{ $t('Performance of contract, consent') }}</td>
					</tr>
					<tr>
						<td class="border border-slate-700 p-2">
							{{ $t('Navigation/Cookie Data - data collected via cookies or similar technologies') }}
						</td>
						<td class="border border-slate-700 p-2">{{ $t('Personalization, analytics') }}</td>
						<td class="border border-slate-700 p-2">{{ $t('Consent, legitimate interest') }}</td>
					</tr>
					<tr>
						<td class="border border-slate-700 p-2">{{ $t('Payment Data (if applicable)') }}</td>
						<td class="border border-slate-700 p-2">{{ $t('Transaction processing') }}</td>
						<td class="border border-slate-700 p-2">{{ $t('Performance of contract') }}</td>
					</tr>
				</tbody>
			</table>
			<p>
				{{ $t('Gaveta Decimal processes the above Personal Data on the Platform for the following purposes')
				}}{{ locale === 'fr' ? ' :' : ':' }}
			</p>

			<h3 class="font-semibold">{{ $t('User Account Creation and Management') }}</h3>
			<ul class="list-disc pl-7 space-y-2.5">
				<li>
					{{
						$t(
							`Purpose: To create, verify, and manage User accounts, enabling them to access and use the Platform's services`,
						)
					}}.
				</li>
				<li>
					{{
						$t(
							'Lawful Basis: Performance of a contract (GDPR Article 6(1)(b)) – this processing is necessary to provide services as agreed in the Terms & Conditions governing the access and use of the Platform',
						)
					}}.
				</li>
			</ul>

			<h3 class="font-semibold">{{ $t('Communication with Users') }}</h3>
			<ul class="list-disc pl-7 space-y-2.5">
				<li>
					{{
						$t(
							`Purpose: To send account-related communications, such as verification emails, password reset links, and important updates concerning the Platform’s Terms & Conditions, Privacy Policy, and Cookies Policy`,
						)
					}}.
				</li>
				<li>
					{{
						$t(
							'Lawful Basis: Performance of a contract (GDPR Article 6(1)(b)) – necessary to ensure the User can access the services provided in the Platform. Legitimate interest (GDPR Article 6(1)(f)) may also be applicable for essential updates that Users need to stay informed about',
						)
					}}.
				</li>
			</ul>

			<h3 class="font-semibold">{{ $t('User Support and Service Assistance') }}</h3>
			<ul class="list-disc pl-7 space-y-2.5">
				<li>{{ $t(`Purpose: To assist Users with queries, technical support, or other assistance requests`) }}.</li>
				<li>
					{{
						$t(
							`Lawful Basis: Performance of a contract (GDPR Article 6(1)(b)) – necessary to fulfill User support obligations under the Platform’s Terms & Conditions`,
						)
					}}.
				</li>
			</ul>

			<h3 class="font-semibold">{{ $t('Marketing Communications (if applicable)') }}</h3>
			<ul class="list-disc pl-7 space-y-2.5">
				<li>{{ $t(`Purpose: To send promotional emails or newsletters (if the User has opted in)`) }}.</li>
				<li>
					{{
						$t(
							'Lawful Basis: Consent (GDPR Article 6(1)(a)) – Users must provide explicit consent to receive marketing communications, which they can withdraw at any time',
						)
					}}.
				</li>
			</ul>

			<h3 class="font-semibold">{{ $t('Legal Compliance') }}</h3>
			<ul class="list-disc pl-7 space-y-2.5">
				<li>
					{{
						$t(
							'Purpose: To comply with legal and regulatory requirements, such as record-keeping obligations, responding to legal requests, or ensuring compliance with tax laws or other regulations',
						)
					}}.
				</li>
				<li>
					{{
						$t(
							'Lawful Basis: Legal obligation (GDPR Article 6(1)(c)) – necessary to comply with applicable laws and regulations that Gaveta Decimal is subject to',
						)
					}}.
				</li>
			</ul>

			<h3 class="font-semibold">{{ $t('Security and Fraud Prevention') }}</h3>
			<ul class="list-disc pl-7 space-y-2.5">
				<li>
					{{ $t(`Purpose: To detect, prevent, and respond to security incidents, fraud, or illegal activities`) }}.
				</li>
				<li>
					{{
						$t(
							`Lawful Basis: Legitimate interest (GDPR Article 6(1)(f)) – protecting Users' accounts and ensuring a secure platform is a legitimate interest that aligns with User expectations and legal obligations`,
						)
					}}.
				</li>
			</ul>
		</div>
	</section>
</template>
